// Crop in the browser; the normal upload path stores the resulting image.
export default async function cropStartpageImage(source, filename, onApply) {
  const t = key => I18n.t(`webshop.startpage_settings.${key}`);
  const image = new Image();
  image.src = source;
  await image.decode();
  const dialog = document.createElement('dialog');
  dialog.className = 'rounded border p-3';
  dialog.style.cssText = 'max-width:95vw;max-height:95vh;overflow:auto';
  const heading = document.createElement('h2'); heading.className = 'fs-5'; heading.textContent = t('crop');
  heading.id = 'startpage-crop-title'; dialog.setAttribute('aria-labelledby', heading.id);
  const help = document.createElement('p'); help.textContent = t('crop_help');
  const canvas = document.createElement('canvas');
  const scale = Math.min(1, Math.min(680, window.innerWidth - 70) / image.naturalWidth, 400 / image.naturalHeight);
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  canvas.style.cssText = 'display:block;max-width:100%;touch-action:none;cursor:crosshair';
  canvas.setAttribute('aria-label', t('crop'));
  const ctx = canvas.getContext('2d');
  const fields = document.createElement('div'); fields.className = 'd-flex flex-wrap gap-2 my-3';
  const inputs = {};
  let selection, origin;
  const normalize = () => {
    selection.x = Math.max(0, Math.min(image.naturalWidth - 1, Math.round(selection.x) || 0));
    selection.y = Math.max(0, Math.min(image.naturalHeight - 1, Math.round(selection.y) || 0));
    selection.width = Math.max(1, Math.min(image.naturalWidth - selection.x, Math.round(selection.width) || 1));
    selection.height = Math.max(1, Math.min(image.naturalHeight - selection.y, Math.round(selection.height) || 1));
  };
  const draw = () => {
    normalize();
    const sx = canvas.width / image.naturalWidth, sy = canvas.height / image.naturalHeight;
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(0,0,0,.55)'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    const {x, y, width, height} = selection;
    ctx.drawImage(image, x, y, width, height, x * sx, y * sy, width * sx, height * sy);
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.strokeRect(x * sx, y * sy, width * sx, height * sy);
    Object.keys(inputs).forEach(key => { inputs[key].value = selection[key]; });
  };
  ['x', 'y', 'width', 'height'].forEach(key => {
    const label = document.createElement('label'); label.textContent = t(`crop_${key}`);
    const input = document.createElement('input'); input.type = 'number'; input.className = 'form-control';
    input.style.width = '110px'; input.min = key === 'x' || key === 'y' ? 0 : 1; input.step = 1;
    input.max = key === 'x' || key === 'width' ? image.naturalWidth : image.naturalHeight;
    input.addEventListener('input', () => { if (input.value === '') return; selection[key] = Number(input.value); draw(); });
    inputs[key] = input; label.append(input); fields.append(label);
  });
  const point = event => {
    const rect = canvas.getBoundingClientRect();
    return {x: Math.max(0, Math.min(image.naturalWidth, (event.clientX - rect.left) * image.naturalWidth / rect.width)),
      y: Math.max(0, Math.min(image.naturalHeight, (event.clientY - rect.top) * image.naturalHeight / rect.height))};
  };
  canvas.addEventListener('pointerdown', event => { origin = point(event); canvas.setPointerCapture(event.pointerId); });
  canvas.addEventListener('pointermove', event => {
    if (!origin) return;
    const end = point(event);
    selection = {x: Math.min(origin.x, end.x), y: Math.min(origin.y, end.y), width: Math.abs(end.x - origin.x), height: Math.abs(end.y - origin.y)};
    draw();
  });
  const endDrag = () => { origin = null; };
  canvas.addEventListener('pointerup', endDrag); canvas.addEventListener('pointercancel', endDrag);
  const actions = document.createElement('div'); actions.className = 'd-flex flex-wrap gap-2';
  const button = (key, style, action) => {
    const element = document.createElement('button'); element.type = 'button'; element.className = `btn ${style}`;
    element.textContent = t(key); element.addEventListener('click', action); actions.append(element); return element;
  };
  const reset = () => { selection = {x: 0, y: 0, width: image.naturalWidth, height: image.naturalHeight}; draw(); };
  button('crop_reset', 'btn-outline-secondary', reset);
  button('crop_cancel', 'btn-outline-secondary', () => dialog.close());
  const error = document.createElement('p'); error.className = 'text-danger mt-2'; error.setAttribute('role', 'alert');
  const apply = button('crop_apply', 'btn-primary', async () => {
    apply.disabled = true;
    try {
      Object.keys(inputs).forEach(key => { selection[key] = Number(inputs[key].value); });
      normalize();
      const output = document.createElement('canvas');
      const ratio = Math.min(1, 2560 / Math.max(selection.width, selection.height));
      output.width = Math.max(1, Math.round(selection.width * ratio)); output.height = Math.max(1, Math.round(selection.height * ratio));
      output.getContext('2d').drawImage(image, selection.x, selection.y, selection.width, selection.height, 0, 0, output.width, output.height);
      const type = /\.jpe?g$/i.test(filename) ? 'image/jpeg' : /\.webp$/i.test(filename) ? 'image/webp' : 'image/png';
      const blob = await new Promise(resolve => output.toBlob(resolve, type, 0.92));
      if (!blob || blob.size > 10 * 1024 * 1024) throw new Error('file size');
      const extension = {'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/png': 'png'}[blob.type];
      onApply(new File([blob], `${filename.replace(/\.[^.]+$/, '')}-crop.${extension}`, {type: blob.type}));
      dialog.close();
    } catch (_) { error.textContent = t('crop_error'); }
    finally { apply.disabled = false; }
  });
  dialog.append(heading, help, canvas, fields, actions, error);
  document.body.append(dialog);
  dialog.addEventListener('close', () => dialog.remove(), {once: true});
  reset(); dialog.showModal();
}
